import { usePrevious } from '@base/utility/CustomHooks';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';

export const MicrophoneStreamContext = createContext(null);

export const MicrophoneStreamProvider = ({ children }) => {
  const [stream, setStream] = useState(null);
  const [rawStream, setRawStream] = useState(null);
  const [gainNode, setGainNode] = useState(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);

  const prevStream = usePrevious(stream);
  useEffect(() => {
    if (prevStream && prevStream !== stream && prevStream.getTracks) {
    prevStream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  }, [prevStream, stream]);

  const prevRawStream = usePrevious(rawStream);
  useEffect(() => {
    if (
      prevRawStream &&
      prevRawStream !== rawStream &&
      prevRawStream.getTracks
    ) {
      prevRawStream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  }, [prevRawStream, rawStream]);

  /** @type {(echoCancellation?: boolean) => void} */
  const startStream = useCallback(async (echoCancellation) => {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia({
          audio: {
            echoCancellation: echoCancellation ?? false,
            // noiseSuppression: false,
            // autoGainControl: false,
          },
        })
        .then((stream) => {
          /**
           * Create a new audio context and build a stream source,
           * stream destination and a gain node. Pass the stream into
           * the mediaStreamSource so we can use it in the Web Audio API.
           */
          setRawStream(stream);
          const context = new AudioContext();
          const mediaStreamSource = context.createMediaStreamSource(stream);
          const mediaStreamDestination = context.createMediaStreamDestination();
          /** @type {GainNode} */
          const gainNode = context.createGain();

          // /**
          //  * Connect the stream to the gainNode so that all audio
          //  * passes through the gain and can be controlled by it.
          //  * Then pass the stream from the gain to the mediaStreamDestination
          //  * which can pass it back to the RTC client.
          //  */
          mediaStreamSource.connect(gainNode);
          gainNode.connect(mediaStreamDestination);
          setGainNode(gainNode);

          /**
           * Use the stream that went through the gainNode. This
           * is the same stream but with altered input volume levels.
           */

          setStream(mediaStreamDestination.stream);
          resolve(mediaStreamDestination.stream);
          return mediaStreamDestination.stream;
        })
        .catch((e) => {
          setStream(null);
          console.error('error starting mic stream:', e);
          setError(e);
          resolve(null);
          return null;
        });
    });
  }, []);

  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  useEffect(() => {
    return () => {
      if (streamRef.current?.getTracks) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, []);

  /** @type {(volume: number) => void} */
  const setVolume = useCallback(
    (volume) => {
      console.log('set volume to ' + volume);
      if (!gainNode) {
        console.error('Gain node not ready to have volume changed');
        return;
      }
      gainNode.gain.value = volume;
    },
    [gainNode]
  );

  return (
    <MicrophoneStreamContext.Provider
      value={{ rawStream, stream, error, setVolume, startStream }}
    >
      {children}
    </MicrophoneStreamContext.Provider>
  );
};

export default MicrophoneStreamProvider;
